import { ProductType } from 'src/type/product';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import { convertGold } from 'src/utils/convert';
import {
  ProductAmountConfigType,
  ProductAmountConfigFormType,
} from 'src/type/product-amount-config';
import axios, { endpoints } from 'src/utils/axios';

const Schema = Yup.object().shape({
  name: Yup.string().required('กรุณากรอกชื่อ'),
  weight: Yup.number()
    .typeError('น้ำหนักต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุน้ำหนัก')
    .moreThan(0, 'จำนวนต้องมากกว่า 0')
    .test('max-decimals', 'ทศนิยมได้มากสุด 4 ตำแหน่ง', function validateMaxDecimals(value) {
      const original = (this as any).originalValue;
      if (original === undefined || original === null || original === '') return true;
      const str = String(original);
      if (!str.includes('.')) return true;
      const [, decimals] = str.split('.');
      return decimals.length <= 4;
    }),
  unitName: Yup.string().required('กรุณาเลือกหน่วยของทอง'),
});

const defaultValues = {
  name: '',
  weight: 0,
  unitName: 'กรัม',
};

type Props = {
  open: boolean;
  onClose: VoidFunction;
  product: ProductType;
  productAmount: ProductAmountConfigType | null;
  handleReload: VoidFunction;
};

export default function ProductAmountFormDialog({
  open,
  onClose,
  product,
  productAmount,
  handleReload,
}: Props) {
  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    watch,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const { unitName } = watch();

  const handleWeightInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: originalValue } = event.target;
    let value = originalValue;
    if (value && value.startsWith('.')) {
      value = `0${value}`;
    }
    // Limit to 4 decimal places during typing
    if (value && value.includes('.')) {
      const [integer, decimals] = value.split('.');
      if (decimals.length > 4) {
        value = `${integer}.${decimals.slice(0, 4)}`;
      }
    }
    methods.setValue('weight', value as unknown as number, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (open && productAmount) {
      reset({
        name: productAmount.name,
        weight:
          productAmount.weightBaht && productAmount.weightBaht > 0 ? productAmount.weightBaht : 0,
        unitName: 'บาท',
      });
    }
  }, [productAmount?.id, open]);

  const isOrnament = product && product?.name.includes('รูปพรรณ');

  const onSubmit = handleSubmit(async (values) => {
    try {
      const body: ProductAmountConfigFormType = {
        productId: product.id,
        name: values.name,
        weightBaht:
          unitName === 'บาท'
            ? Math.round(Number(values.weight) * 10000) / 10000
            : Math.round(convertGold(Number(values.weight), 'weightBaht', isOrnament) * 10000) /
              10000,
        weightGram:
          unitName === 'กรัม'
            ? Math.round(Number(values.weight) * 10000) / 10000
            : Math.round(convertGold(Number(values.weight), 'weightGrams', isOrnament) * 10000) /
              10000,
      };

      // console.log('body', body);

      if (productAmount) {
        await axios.patch(endpoints.productAmountConfig.byId(productAmount.id), body);
      } else {
        await axios.post(endpoints.productAmountConfig.main, body);
      }
      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const isBar96 = product && product?.name.includes('แท่ง') && product?.name.includes('96');

  // useEffect(() => {
  //   if (!isBar96) {
  //     setValue('unitName', 'กรัม');
  //   }
  // }, [isBar96]);

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{productAmount ? 'แก้ไข' : 'สร้าง'}จำนวนทอง</DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            <RHFTextField
              label="ชื่อ"
              name="name"
              placeholder="ชื่อ"
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />

            <RHFTextField
              label={`น้ำหนัก (${unitName})`}
              name="weight"
              placeholder={`น้ำหนัก (${unitName})`}
              InputLabelProps={{ shrink: true }}
              type="text"
              inputProps={{ inputMode: 'decimal', pattern: '^\\d*(\\.\\d{0,4})?$' }}
              onChange={handleWeightInput}
            />

            {!!isBar96 && (
              <RHFSelect name="unitName" label="หน่วยทองคำ">
                <MenuItem value="บาท">บาท</MenuItem>
                <MenuItem value="กรัม">กรัม</MenuItem>
              </RHFSelect>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
